/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { useEffect } from 'react';
import Monster from 'model/monster/Monster';

export interface BlockPositions {
  blockStartAbility: number;
  blockStartActions: number;
  blockStartSpells: number;
  blockStartBonusActions: number;
  blockStartReaction: number;
  blockStartLegendary: number;
  blockStartMythic: number;
  blockStartRegional: number;
  blockStartLair: number;
  blockStartInventory: number;
  blockStartNext: number;
}

const useBlockPositions = (
  mon: Monster,
  showAll: boolean,
  handleUpdateMonster: (field: string, value: any) => void,
): BlockPositions => {
  const showEmptyHeader = showAll ? 1: 0;
  const blockStartAbility      = 0;
  const blockStartActions      = blockStartAbility      + (mon.traits?.length || showEmptyHeader);
  const blockStartSpells       = blockStartActions      + (mon.actions?.length || showEmptyHeader);
  const blockStartBonusActions = blockStartSpells       + (mon.spells?.length || showEmptyHeader);
  const blockStartReaction     = blockStartBonusActions + (mon.bonus_actions?.length || showEmptyHeader);
  const blockStartLegendary    = blockStartReaction     + (mon.reactions?.length || showEmptyHeader);

  let blockStartMythic         = blockStartLegendary    + (mon.legendary_actions?.length || showEmptyHeader);
  if ((mon.legendary_actions?.length > 0) && (showAll || mon.legendary_desc)) blockStartMythic++;

  let blockStartRegional       = blockStartMythic       + (mon.mythic_actions?.length || showEmptyHeader);
  if ((mon.mythic_actions?.length > 0) && (showAll || mon.mythic_desc)) blockStartRegional++;

  let blockStartLair           = blockStartRegional     + (mon.regional_actions?.length || showEmptyHeader);
  if ((mon.regional_actions?.length > 0) && (showAll || mon.regional_desc))     blockStartLair++;
  if ((mon.regional_actions?.length > 0) && (showAll || mon.regional_desc_end)) blockStartLair++;

  let blockStartInventory      = blockStartLair         + (mon.lair_actions?.length || showEmptyHeader);
  if ((mon.lair_actions?.length > 0) && (showAll || mon.lair_desc))     blockStartInventory++;
  if ((mon.lair_actions?.length > 0) && (showAll || mon.lair_desc_end)) blockStartInventory++;

  const blockStartNext           = blockStartInventory    + (mon.inventory?.length || showEmptyHeader);

  useEffect(() => {
    if (mon.splitPoint > blockStartNext) {
      handleUpdateMonster('splitPoint', blockStartNext);
    }
  }, [blockStartNext, mon.splitPoint]);

  return {
    blockStartAbility,
    blockStartActions,
    blockStartSpells,
    blockStartBonusActions,
    blockStartReaction,
    blockStartLegendary,
    blockStartMythic,
    blockStartRegional,
    blockStartLair,
    blockStartInventory,
    blockStartNext,
  };
};

export default useBlockPositions;
